import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import UserMenu from "../components/UserMenu";
import SafetyCommandPalette from "./SafetyCommandPalette";
import {
  ChevronRight,
  Search,
  ArrowRightLeft,
  Activity,
  Factory,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import "./safetyTheme.css";

/**
 * SafetyShell — the unified chrome for the Safety module.
 *
 * Props:
 *   user, onLogout
 *   breadcrumb: [{ label, to? }]   // last item rendered as current (no link)
 *   rightRail: ReactNode | null
 *   miniMap: [{ label, to?, active?, depth? }]
 *   children: main content
 */
export default function SafetyShell({
  user,
  onLogout,
  breadcrumb = [],
  rightRail = null,
  miniMap = [],
  children,
}) {
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(false);

  // Cmd/Ctrl+K to open palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="safety-shell" data-testid="safety-shell">
      <Toaster position="top-right" richColors />
      <SafetyCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

      {/* TOP CHROME */}
      <div className="sf-top-chrome">
        <div className="max-w-[1400px] mx-auto h-14 px-5 flex items-center gap-4">
          <button
            onClick={() => navigate("/safety")}
            className="flex items-center gap-2"
            data-testid="safety-home-btn"
          >
            <Factory className="w-5 h-5" style={{ color: "var(--sf-accent-hover)" }} />
            <span className="font-bold tracking-tight" style={{ fontFamily: "var(--sf-font-heading)" }}>
              SAFETY
            </span>
          </button>
          <span className="text-zinc-300">·</span>
          <button
            onClick={() => navigate("/")}
            className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
            data-testid="module-switch-btn"
          >
            <ArrowRightLeft className="w-3 h-3" /> Switch to Project
          </button>

          {/* Command palette trigger */}
          <button
            onClick={() => setPaletteOpen(true)}
            className="ml-auto md:ml-8 flex-1 max-w-md hidden md:flex items-center gap-2 h-9 px-3 border border-zinc-200 bg-zinc-50 hover:bg-white text-sm text-zinc-500 transition-colors"
            data-testid="global-search-cmd-k"
          >
            <Search className="w-4 h-4" />
            <span>Search PO · Report · Customer · Batch …</span>
            <span className="ml-auto text-[10px] tracking-wider px-1.5 py-0.5 border border-zinc-300 rounded-sm font-mono">
              ⌘K
            </span>
          </button>

          <Button
            onClick={() => navigate("/audit")}
            variant="ghost"
            size="sm"
            className="hidden md:inline-flex"
            data-testid="audit-nav-btn"
          >
            <Activity className="w-4 h-4 mr-1.5" /> Audit
          </Button>
          <UserMenu user={user} onLogout={onLogout} variant="header" />
        </div>
      </div>

      {/* BREADCRUMB BAR */}
      {breadcrumb.length > 0 && (
        <div className="sf-breadcrumb-bar">
          <div className="max-w-[1400px] mx-auto px-5 h-10 flex items-center text-sm" data-testid="breadcrumb-bar">
            {breadcrumb.map((b, i) => {
              const isLast = i === breadcrumb.length - 1;
              return (
                <div key={i} className="flex items-center">
                  {i > 0 && <ChevronRight className="w-3.5 h-3.5 mx-1.5 text-zinc-300" />}
                  {isLast || !b.to ? (
                    <span className="font-semibold text-zinc-900" data-testid={`breadcrumb-current`}>
                      {b.label}
                    </span>
                  ) : (
                    <button
                      onClick={() => navigate(b.to)}
                      className="text-zinc-500 hover:text-zinc-900"
                      data-testid={`breadcrumb-link-${i}`}
                    >
                      {b.label}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MAIN GRID: minimap | content | right rail */}
      <div className="max-w-[1400px] mx-auto px-5 py-6">
        <div
          className="sf-shell-grid"
          style={{
            "--sf-left": railCollapsed ? "40px" : (miniMap.length > 0 ? "220px" : "0px"),
            "--sf-right": rightRail ? "300px" : "0px",
          }}
        >
          {/* LEFT MINI-MAP */}
          {miniMap.length > 0 && (
            <aside className="hidden lg:block" data-testid="left-minimap">
              {railCollapsed ? (
                <button
                  onClick={() => setRailCollapsed(false)}
                  className="w-9 h-9 flex items-center justify-center border border-zinc-200 bg-white hover:bg-zinc-50"
                  data-testid="minimap-expand-btn"
                  title="Expand"
                >
                  <PanelLeftOpen className="w-4 h-4" />
                </button>
              ) : (
                <div className="sf-minimap">
                  <div className="flex items-center justify-between mb-2">
                    <span className="sf-label">Drill Path</span>
                    <button
                      onClick={() => setRailCollapsed(true)}
                      className="text-zinc-400 hover:text-zinc-700"
                      data-testid="minimap-collapse-btn"
                      title="Collapse"
                    >
                      <PanelLeftClose className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {miniMap.map((n, i) => (
                    <div
                      key={i}
                      className={`sf-minimap-node ${n.active ? "active" : ""}`}
                      style={{ paddingLeft: 8 + (n.depth || 0) * 12 }}
                      onClick={() => n.to && navigate(n.to)}
                      data-testid={`minimap-node-${i}`}
                    >
                      <ChevronRight className="w-3 h-3" />
                      {n.label}
                    </div>
                  ))}
                </div>
              )}
            </aside>
          )}

          {/* MAIN */}
          <main className="min-w-0" data-testid="safety-main">
            {children}
          </main>

          {/* RIGHT RAIL */}
          {rightRail && (
            <aside className="hidden lg:block" data-testid="right-rail">
              {rightRail}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
}
